import React, { useCallback, useContext } from 'react'
import PriceRowPointsSpend from '../PriceRowPointsSpend'
import Group from 'components/utils/Group'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import PriceRowPrice from '../PriceRowPrice'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import styled from 'styled-components'
import Divider from 'LuxKit/Divider'
import PriceRowSale from '../PriceRowSale'
import PriceRowPointsSale from '../PriceRowPointsSale'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import PriceRowLoyaltyPoints from '../Loyalty/PriceRowLoyaltyPoints'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import ModalContext from 'contexts/ModalContext'
import LoyaltyGenericInfoModal from 'loyalty/components/LoyaltyGenericInfoModal'

const OrText = styled(BodyText)`
  line-height: ${rem(20)};
`

/** This height is required to align the tiles’ divider horizontally. */
const PointsContainer = styled.div`
  height: ${rem(65)};
`

interface Props {
  /**
   * The current price for the item
   * e.g. may be a sale price or regular price
   */
  price: number
  /**
   * The original/regular price for the item
   *
   * If this is different than the current price
   * the stack will show the 'sale' variant
   */
  regularPrice: number
  /**
   * Current price of the item in points
   * e.g. may be sale points or regular points
   */
  points?: number
  /**
   * The original/regular price of the item in points
   */
  pointsRegularPrice: number
  direction: 'horizontal' | 'stack'
  size: 'S' | 'L'
  variant: App.MerchandiseOfferVariant
}

function PriceRowMerchandiseCardPriceStack(props: Props) {
  const {
    price,
    points,
    size,
    direction,
    regularPrice,
    pointsRegularPrice,
    variant,
  } = props

  const [tier] = useCurrentLoyaltyTier()
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const showModal = useContext(ModalContext)

  const handleInfoClick = useCallback(() => {
    showModal(<LoyaltyGenericInfoModal />)
  }, [showModal])

  const isOnSale = price !== regularPrice
  const isOnPointsSale = points !== pointsRegularPrice

  const showEarnPoints = isLoyaltyEnabled && !!variant.pointsEarnRatio

  return <Group
    direction={direction === 'horizontal' ? 'horizontal' : 'vertical'}
    gap={16}
    verticalAlign={direction === 'horizontal' ? 'start' : undefined}
  >
    <div>
      <Group direction="horizontal" gap={8} verticalAlign="start">
        <LoyaltyTierIcon tier={tier} size="S" />
        {!!points && <PriceRowPointsSpend points={points} size={size} />}
      </Group>
      {isOnPointsSale && <PriceRowPointsSale value={pointsRegularPrice} colour="neutral-three" />}
    </div>

    <Divider kind="primary" />

    <PointsContainer>
      <Group direction="vertical" gap={4}>
        {!!price && <Group direction="horizontal" gap={8} verticalAlign="start">
          {!!points && <OrText variant="small" colour="neutral-three">or</OrText>}
          <PriceRowPrice
            data-testid="merch-pdp-price-amount"
            price={price}
            size="L"
            saleUnit=""
            priceFormat="dynamicDollar"
            priceStyle="body-text"
          />
        </Group>}
        {isOnSale && <PriceRowSale value={regularPrice} colour="neutral-three" priceFormat="dynamicDollar" />}
        {showEarnPoints && (
          <PriceRowLoyaltyPoints
            tier={tier}
            size="S"
            earnablePoints={0}
            pointsEarnRatio={variant.pointsEarnRatio}
            calculationType="ratio"
            onInfoIconClick={handleInfoClick}
          />
        )}
      </Group>
    </PointsContainer>
  </Group>
}

export default PriceRowMerchandiseCardPriceStack
